import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { cmsService } from '@services/api'
import { 
  User, 
  Plus, 
  Search, 
  Filter, 
  CheckCircle, 
  Download,
  Star,
  Phone,
  Mail,
  Car,
  Calendar,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  AlertCircle,
  Clock,
  Key,
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  CardHeader,
  Center,
  Flex,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  SimpleGrid,
  Box,
  Avatar,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Divider,
  Tooltip,
  Spinner,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'

// Types for driver data
interface Driver {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  role: string;
  status: 'active' | 'inactive' | 'suspended';
  licenseNumber?: string;
  licenseExpiry?: string;
  createdAt: string;
  updatedAt: string;
}

interface DriverFormData {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  password: string
  licenseNumber: string
  licenseExpiry: string
}

// View Driver Modal
function DriverViewModal({ isOpen, onClose, driver }: { isOpen: boolean; onClose: () => void; driver: Driver | null }) {
  if (!driver) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="green.100" borderRadius="lg">
              <UserCheck size={20} color="var(--chakra-colors-green-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold">{driver.firstName} {driver.lastName}</Text>
              <Text fontSize="sm" color="gray.600">Driver Details</Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Full Name</Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                      {driver.firstName} {driver.lastName}
                    </Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Status</Text>
                    <Badge
                      colorScheme={
                        driver.status === 'active' ? 'green' :
                        driver.status === 'inactive' ? 'gray' : 'red'
                      }
                      variant="subtle"
                      borderRadius="full"
                      px={3}
                      py={1}
                      textTransform="capitalize"
                    >
                      {driver.status}
                    </Badge>
                  </VStack>
                </SimpleGrid>
              </CardBody>
            </Card>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <Mail size={16} color="var(--chakra-colors-gray-600)" />
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Email</Text>
                    </HStack>
                    <Text fontSize="md" fontWeight="semibold" color="gray.900">{driver.email}</Text>
                  </VStack>
                </CardBody>
              </Card>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <Phone size={16} color="var(--chakra-colors-gray-600)" />
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Phone</Text>
                    </HStack>
                    <Text fontSize="md" fontWeight="semibold" color="gray.900">{driver.phoneNumber}</Text>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>

            {driver.licenseNumber && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                  <CardBody>
                    <VStack align="start" spacing={2}>
                      <HStack spacing={2}>
                        <Car size={16} color="var(--chakra-colors-gray-600)" />
                        <Text fontSize="sm" fontWeight="medium" color="gray.600">License Number</Text>
                      </HStack>
                      <Text fontSize="md" fontWeight="semibold" color="gray.900">{driver.licenseNumber}</Text>
                    </VStack>
                  </CardBody>
                </Card>
                {driver.licenseExpiry && (
                  <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                    <CardBody>
                      <VStack align="start" spacing={2}>
                        <HStack spacing={2}>
                          <Calendar size={16} color="var(--chakra-colors-gray-600)" />
                          <Text fontSize="sm" fontWeight="medium" color="gray.600">License Expiry</Text>
                        </HStack>
                        <Text fontSize="md" fontWeight="semibold" color="gray.900">
                          {new Date(driver.licenseExpiry).toLocaleDateString()}
                        </Text>
                      </VStack>
                    </CardBody>
                  </Card>
                )}
              </SimpleGrid>
            )}

            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody>
                <VStack align="start" spacing={2}>
                  <HStack spacing={2}>
                    <Clock size={16} color="var(--chakra-colors-gray-600)" />
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Member Since</Text>
                  </HStack>
                  <Text fontSize="md" fontWeight="semibold" color="gray.900">
                    {new Date(driver.createdAt).toLocaleDateString()}
                  </Text>
                </VStack>
              </CardBody>
            </Card>

            <HStack spacing={3} w="full" pt={4}>
              <Button variant="outline" onClick={onClose} flex={1} borderRadius="xl">
                Close
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// Edit Driver Modal
function DriverEditModal({ isOpen, onClose, driver }: { isOpen: boolean; onClose: () => void; driver: Driver | null }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    licenseNumber: '',
    licenseExpiry: '',
    status: 'active' as 'active' | 'inactive' | 'suspended'
  })
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  React.useEffect(() => {
    if (driver) {
      setFormData({
        firstName: driver.firstName || '',
        lastName: driver.lastName || '',
        email: driver.email || '',
        phoneNumber: driver.phoneNumber || '',
        licenseNumber: driver.licenseNumber || '',
        licenseExpiry: driver.licenseExpiry || '',
        status: driver.status || 'active'
      })
    }
  }, [driver])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      if (!driver) return

      // Update driver data
      await cmsService.updateDriver(driver.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        licenseNumber: formData.licenseNumber || undefined,
        licenseExpiry: formData.licenseExpiry || undefined,
      })

      // Update status if changed
      if (formData.status !== driver.status) {
        await cmsService.updateDriverStatus(driver.id, formData.status)
      }

      queryClient.invalidateQueries('drivers')
      queryClient.invalidateQueries('dashboard-stats')
      
      toast({
        title: 'Driver Updated',
        description: `${formData.firstName} ${formData.lastName} has been updated successfully.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      
      onClose()
    } catch (error: any) {
      const errorData = error?.response?.data?.error;
      let errorMessage = 'Failed to update driver.';
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData && typeof errorData.message === 'string') {
        errorMessage = errorData.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!driver) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="green.100" borderRadius="lg">
              <Edit size={20} color="var(--chakra-colors-green-600)" />
            </Box>
            <Text fontSize="xl" fontWeight="bold">Edit Driver</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">First Name</FormLabel>
                  <Input
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    placeholder="First Name"
                    borderRadius="xl"
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Last Name</FormLabel>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    placeholder="Last Name"
                    borderRadius="xl"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@example.com"
                  borderRadius="xl"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">Phone Number</FormLabel>
                <Input
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                  placeholder="+255 XXX XXX XXX"
                  borderRadius="xl"
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">License Number</FormLabel>
                  <Input
                    value={formData.licenseNumber}
                    onChange={(e) => setFormData({...formData, licenseNumber: e.target.value})}
                    placeholder="License Number"
                    borderRadius="xl"
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">License Expiry</FormLabel>
                  <Input
                    type="date"
                    value={formData.licenseExpiry}
                    onChange={(e) => setFormData({...formData, licenseExpiry: e.target.value})}
                    borderRadius="xl"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">Status</FormLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                  borderRadius="xl"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </Select>
              </FormControl>

              <HStack spacing={3} w="full" pt={4}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  flex={1}
                  borderRadius="xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="brand"
                  flex={1}
                  borderRadius="xl"
                  isLoading={isLoading}
                  loadingText="Updating..."
                  leftIcon={<Edit size={16} />}
                >
                  Update Driver
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function DriverFormModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [formData, setFormData] = useState<DriverFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    licenseNumber: '',
    licenseExpiry: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  const createDriverMutation = useMutation(
    (driverData: any) => cmsService.createDriver(driverData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('drivers')
        toast({
          title: 'Driver Added',
          description: `${formData.firstName} ${formData.lastName} has been successfully added.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onClose()
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          password: '',
          licenseNumber: '',
          licenseExpiry: ''
        })
      },
      onError: (error: any) => {
        const errorData = error?.response?.data?.error;
        let errorMessage = 'Failed to add driver. Please try again.';
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        toast({
          title: 'Error',
          description: errorMessage,
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    }
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    
    try {
      await createDriverMutation.mutateAsync(formData)
    } catch (err) {
      // Error handled by useMutation.onError
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add New Driver</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <HStack spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                </FormControl>
              </HStack>

                <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  placeholder="Enter phone number"
                  />
                </FormControl>

                <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                />
              </FormControl>

              <HStack spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>License Number</FormLabel>
                  <Input
                    value={formData.licenseNumber}
                    onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                    placeholder="Enter license number"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>License Expiry</FormLabel>
                  <Input
                    type="date"
                    value={formData.licenseExpiry}
                    onChange={(e) => setFormData({ ...formData, licenseExpiry: e.target.value })}
                  />
                </FormControl>
              </HStack>

              <HStack spacing={4} w="full" pt={4}>
                <Button variant="outline" onClick={onClose} flex={1}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="brand"
                  isLoading={isLoading}
                  loadingText="Adding..."
                  flex={1}
                >
                  Add Driver
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// Delete Driver Confirmation Dialog
function DriverDeleteDialog({ isOpen, onClose, driver, onConfirm }: { 
  isOpen: boolean; 
  onClose: () => void; 
  driver: Driver | null;
  onConfirm: () => void;
}) {
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = async () => {
    setIsDeleting(true)
    await onConfirm()
    setIsDeleting(false)
  }

  if (!driver) return null

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Delete Driver
          </AlertDialogHeader>

          <AlertDialogBody>
            Are you sure you want to delete <strong>{driver.firstName} {driver.lastName}</strong>? 
            This action cannot be undone and will remove all associated data.
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={handleDelete} 
              ml={3}
              isLoading={isDeleting}
              loadingText="Deleting..."
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

// Reset Password Modal
function DriverResetPasswordModal({ isOpen, onClose, driver }: { isOpen: boolean; onClose: () => void; driver: Driver | null }) {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setPassword('')
      setConfirmPassword('')
      setShowPassword(false)
      setShowConfirmPassword(false)
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!driver) return

    // Validation
    if (password.length < 8) {
      toast({
        title: 'Invalid Password',
        description: 'Password must be at least 8 characters long.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Password Mismatch',
        description: 'Passwords do not match.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsLoading(true)

    try {
      await cmsService.resetDriverPassword(driver.id, password, confirmPassword)
      
      toast({
        title: 'Password Reset',
        description: `Password for ${driver.firstName} ${driver.lastName} has been reset successfully.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      
      onClose()
    } catch (error: any) {
      const errorData = error?.response?.data?.error;
      let errorMessage = 'Failed to reset password.';
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData && typeof errorData.message === 'string') {
        errorMessage = errorData.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!driver) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'md' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="blue.100" borderRadius="lg">
              <User size={20} color="var(--chakra-colors-blue-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold">Reset Password</Text>
              <Text fontSize="sm" color="gray.600">{driver.firstName} {driver.lastName}</Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">New Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                    borderRadius="xl"
                  />
                  <Button
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    variant="ghost"
                    position="absolute"
                    right={2}
                    top="50%"
                    transform="translateY(-50%)"
                  >
                    {showPassword ? <Eye size={16} /> : <Eye size={16} />}
                  </Button>
                </InputGroup>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Password must be at least 8 characters long
                </Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">Confirm Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    borderRadius="xl"
                  />
                  <Button
                    size="sm"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    variant="ghost"
                    position="absolute"
                    right={2}
                    top="50%"
                    transform="translateY(-50%)"
                  >
                    {showConfirmPassword ? <Eye size={16} /> : <Eye size={16} />}
                  </Button>
                </InputGroup>
              </FormControl>

              <HStack spacing={3} w="full" pt={4}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  flex={1}
                  borderRadius="xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  flex={1}
                  borderRadius="xl"
                  isLoading={isLoading}
                  loadingText="Resetting..."
                >
                  Reset Password
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default function DriversPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const { isOpen: isResetPasswordOpen, onOpen: onResetPasswordOpen, onClose: onResetPasswordClose } = useDisclosure()
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null)
  const toast = useToast()
  const queryClient = useQueryClient()

  // Fetch real data from API
  const { data: driversData, isLoading: driversLoading } = useQuery(
    'drivers',
    () => cmsService.getDrivers(),
    {
      retry: false,
    }
  )

  const { data: dashboardStats } = useQuery(
    'dashboard-stats',
    () => cmsService.getDashboardStats(),
    {
      retry: false,
    }
  )

  // Use real data only - no fallbacks
  const drivers: Driver[] = driversData?.data || []
  
  const stats = [
    { title: "Total Drivers", value: dashboardStats?.data?.totalDrivers || 0, color: "blue", icon: User },
    { title: "Active Drivers", value: dashboardStats?.data?.assignedDrivers || 0, color: "green", icon: CheckCircle },
    { title: "Available", value: dashboardStats?.data?.availableDrivers || 0, color: "orange", icon: UserCheck },
    { title: "Suspended", value: drivers.filter((d: Driver) => d.status === 'suspended').length, color: "red", icon: AlertCircle },
  ]

  const filteredDrivers = drivers.filter((driver: Driver) => {
    const fullName = `${driver.firstName} ${driver.lastName}`.toLowerCase()
    const email = driver.email.toLowerCase()
    const phone = driver.phoneNumber.toLowerCase()
    
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                         email.includes(searchTerm.toLowerCase()) ||
                         phone.includes(searchTerm.toLowerCase())
    const matchesStatus = selectedStatus === 'all' || driver.status === selectedStatus
    return matchesSearch && matchesStatus
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredDrivers.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentDrivers = filteredDrivers.slice(startIndex, endIndex)

  const handleViewDriver = (driver: Driver) => {
    setSelectedDriver(driver)
    onViewOpen()
  }

  const handleEditDriver = (driver: Driver) => {
    setSelectedDriver(driver)
    onEditOpen()
  }

  const handleDeleteDriver = (driver: Driver) => {
    setSelectedDriver(driver)
    onDeleteOpen()
  }

  const handleResetPassword = (driver: Driver) => {
    setSelectedDriver(driver)
    onResetPasswordOpen()
  }

  const confirmDeleteDriver = async () => {
    if (!selectedDriver) return

    try {
      await cmsService.deleteDriver(selectedDriver.id)
      queryClient.invalidateQueries('drivers')
      queryClient.invalidateQueries('dashboard-stats')
      
      toast({
        title: 'Driver Deleted',
        description: `${selectedDriver.firstName} ${selectedDriver.lastName} has been deleted successfully.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      
      onDeleteClose()
    } catch (error: any) {
      const errorData = error?.response?.data?.error;
      let errorMessage = 'Failed to delete driver.';
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData && typeof errorData.message === 'string') {
        errorMessage = errorData.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green'
      case 'inactive': return 'gray'
      case 'suspended': return 'red'
      default: return 'gray'
    }
  }

  const handleStatusChange = async (driverId: string, newStatus: 'active' | 'inactive' | 'suspended') => {
    try {
      await cmsService.updateDriverStatus(driverId, newStatus)
      queryClient.invalidateQueries('drivers')
      queryClient.invalidateQueries('dashboard-stats')
      toast({
        title: 'Status Updated',
        description: 'Driver status has been updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error: any) {
      const errorData = error?.response?.data?.error;
      let errorMessage = 'Failed to update driver status.';
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData && typeof errorData.message === 'string') {
        errorMessage = errorData.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // Show loading state
  if (driversLoading) {
    return (
      <Center h="64">
        <Spinner size="lg" color="brand.500" />
      </Center>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align={{ base: 'center', md: 'start' }} spacing={2}>
          <Heading size="xl" color="gray.900">
            Drivers Management
          </Heading>
          <Text color="gray.600">
            Manage your driver fleet and track their performance
          </Text>
        </VStack>
        <HStack spacing={3}>
            <Button
            leftIcon={<Download size={16} />}
              variant="outline"
            size="sm"
          >
            Export
            </Button>
          <Button
            leftIcon={<Plus size={16} />}
            colorScheme="brand"
            onClick={onOpen}
          >
          Add Driver
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
        {stats.map((stat, index) => (
          <Card key={index} bg="white" shadow="sm">
            <CardBody>
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">
                    {stat.title}
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.900">
                    {stat.value}
                  </Text>
                </VStack>
                <Box
                  p={3}
                  borderRadius="lg"
                  bg={`${stat.color}.100`}
                  color={`${stat.color}.600`}
                >
                  <stat.icon size={24} />
                </Box>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {/* Filters and Search */}
      <Card bg="white" shadow="sm">
        <CardBody>
          <HStack spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                  <Search size={16} color="var(--chakra-colors-gray-400)" />
                </InputLeftElement>
                <Input
                placeholder="Search drivers by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Select
              maxW="200px"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
              <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </Select>
            </HStack>
        </CardBody>
      </Card>

      {/* Drivers Table */}
      <Card bg="white" shadow="sm">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md" color="gray.900">
              Drivers List
            </Heading>
            <Text color="gray.600" fontSize="sm">
              {filteredDrivers.length} driver{filteredDrivers.length !== 1 ? 's' : ''}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Driver</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Contact</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>License</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Status</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Joined</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredDrivers.length === 0 ? (
                  <Tr>
                    <Td colSpan={6} py={12}>
                      <Center>
                        <VStack spacing={4}>
                          <Box p={4} bg="gray.100" borderRadius="full">
                            <User size={48} color="var(--chakra-colors-gray-500)" />
                          </Box>
                          <VStack spacing={2}>
                            <Heading size="md" color="gray.900">
                              No drivers found
                            </Heading>
                            <Text color="gray.600" textAlign="center">
                              {searchTerm || selectedStatus !== 'all' 
                                ? 'No drivers match your current filters.'
                                : 'Get started by adding your first driver.'
                              }
                            </Text>
                          </VStack>
                          {!searchTerm && selectedStatus === 'all' && (
                            <Button 
                              colorScheme="brand" 
                              leftIcon={<Plus size={16} />}
                              onClick={onOpen}
                              borderRadius="xl"
                            >
                              Add Your First Driver
                            </Button>
                          )}
                        </VStack>
                      </Center>
                    </Td>
                  </Tr>
                ) : (
                  currentDrivers.map((driver: Driver) => (
                  <Tr key={driver.id} _hover={{ bg: 'gray.50' }} borderBottom="1px solid" borderColor="gray.100">
                    <Td py={4}>
                      <HStack spacing={3}>
                          <Avatar size="sm" bg="blue.100" color="blue.600" icon={<User size={16} />} />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium" color="gray.900">
                              {driver.firstName} {driver.lastName}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                              {driver.email}
                          </Text>
                        </VStack>
                      </HStack>
                    </Td>
                    <Td py={4}>
                      <VStack align="start" spacing={1}>
                        <HStack spacing={2}>
                            <Phone size={12} color="var(--chakra-colors-gray-500)" />
                          <Text fontSize="sm" color="gray.900">
                              {driver.phoneNumber}
                            </Text>
                          </HStack>
                      </VStack>
                    </Td>
                    <Td py={4}>
                      <VStack align="start" spacing={1}>
                          <Text fontSize="sm" color="gray.900">
                            {driver.licenseNumber || 'N/A'}
                        </Text>
                          {driver.licenseExpiry && (
                        <Text fontSize="xs" color="gray.500">
                              Expires: {new Date(driver.licenseExpiry).toLocaleDateString()}
                            </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td py={4}>
                        <Badge
                          colorScheme={getStatusColor(driver.status)}
                          variant="subtle"
                          px={3}
                          py={1}
                          borderRadius="full"
                          textTransform="capitalize"
                        >
                          {driver.status}
                        </Badge>
                      </Td>
                      <Td py={4}>
                        <Text fontSize="sm" color="gray.600">
                          {new Date(driver.createdAt).toLocaleDateString()}
                        </Text>
                    </Td>
                    <Td py={4}>
                      <HStack spacing={1}>
                        <Tooltip label="View Details">
                          <IconButton
                            aria-label="View"
                            icon={<Eye size={16} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleViewDriver(driver)}
                          />
                        </Tooltip>
                        <Tooltip label="Edit Driver">
                          <IconButton
                            aria-label="Edit"
                            icon={<Edit size={16} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleEditDriver(driver)}
                          />
                        </Tooltip>
                        <Tooltip label="Reset Password">
                          <IconButton
                            aria-label="Reset Password"
                            icon={<Key size={16} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="purple"
                            onClick={() => handleResetPassword(driver)}
                          />
                        </Tooltip>
                        <Tooltip label="Delete Driver">
                          <IconButton
                            aria-label="Delete"
                            icon={<Trash2 size={16} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDeleteDriver(driver)}
                          />
                        </Tooltip>
                          <Tooltip label="Change Status">
                            <Select
                              size="sm"
                              value={driver.status}
                              onChange={(e) => handleStatusChange(driver.id, e.target.value as any)}
                              w="120px"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                              <option value="suspended">Suspended</option>
                            </Select>
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <HStack justify="center" py={4} borderTop="1px solid" borderColor="gray.100">
                <Button
                  size="sm"
                  variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  isDisabled={currentPage === 1}
                >
                  Previous
                </Button>
            <Text fontSize="sm" color="gray.600" px={4}>
              Page {currentPage} of {totalPages}
            </Text>
                <Button
                  size="sm"
                  variant="outline"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  isDisabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </HStack>
        )}
      </Card>

      {/* Add Driver Modal */}
      <DriverFormModal isOpen={isOpen} onClose={onClose} />
      
      {/* Driver View Modal */}
      <DriverViewModal isOpen={isViewOpen} onClose={onViewClose} driver={selectedDriver} />
      
      {/* Driver Edit Modal */}
      <DriverEditModal isOpen={isEditOpen} onClose={onEditClose} driver={selectedDriver} />
      
      {/* Driver Delete Dialog */}
      <DriverDeleteDialog 
        isOpen={isDeleteOpen} 
        onClose={onDeleteClose} 
        driver={selectedDriver}
        onConfirm={confirmDeleteDriver}
      />
      
      {/* Driver Reset Password Modal */}
      <DriverResetPasswordModal 
        isOpen={isResetPasswordOpen} 
        onClose={onResetPasswordClose} 
        driver={selectedDriver}
      />
    </VStack>
  )
}